// Offline floor-catalogue store (Module 01 S9, §4/§9.14). The floor PWA syncs the
// facility's active products + active routings from GET /v1/catalogue/floor-cache
// — a delta feed with a `since` cursor and `deleted` tombstones — and persists
// the merged snapshot to localStorage so the lookup card works with no network.
//
// This module is PURE data + storage: the merge (`mergeFloorDelta`) is exported
// and unit-tested; persistence is guarded so a disabled/absent localStorage never
// throws inside a render. Cost is never part of this cache — the endpoint strips
// all cost fields unconditionally (§7 / §13.4), and nothing here reads them.

import type { components } from "@assessly/api-client";

type Product = components["schemas"]["Product"];
type Routing = components["schemas"]["Routing"];
type FloorCacheResponse = components["schemas"]["FloorCacheResponse"];

/** The merged, persisted view of one facility's floor catalogue. */
export interface FloorSnapshot {
  facilityId: string;
  /** Active products for the facility, keyed by id after the delta merge. */
  products: Product[];
  /** The currently-active routing per product for the facility (one each). */
  routings: Routing[];
  /** Opaque delta cursor to hand back as `?since=` on the next sync. */
  since: string;
  /** Epoch ms of the last successful merge — drives the cache-age banner. */
  syncedAt: number;
}

const SNAPSHOT_PREFIX = "assessly.floor.catalogue.v1:";
const FACILITY_KEY = "assessly.floor.facilityId";

function snapshotKey(facilityId: string): string {
  return `${SNAPSHOT_PREFIX}${facilityId}`;
}

// ---- delta merge (pure) -----------------------------------------------------

/**
 * Fold a floor-cache delta into the prior snapshot for the same facility.
 *
 * - products/routings in the delta upsert by id (a routing replaces the prior
 *   active version for the same product — the client-side "new version replaces"
 *   rule from §5's floor-cache row);
 * - `deleted.product_ids` / `deleted.routing_ids` are tombstones — evicted;
 * - routings orphaned by a product eviction are pruned (a discontinued product
 *   must not surface a routing on the card);
 * - `next_since` becomes the new cursor and `now` the sync timestamp.
 *
 * When `prior` is absent or belongs to a different facility the delta is treated
 * as a full snapshot (the caller omits `?since=` in that case).
 */
export function mergeFloorDelta(
  prior: FloorSnapshot | undefined,
  delta: FloorCacheResponse,
  facilityId: string,
  now: number,
): FloorSnapshot {
  const productsById = new Map<string, Product>();
  const routingByProduct = new Map<string, Routing>();

  if (prior !== undefined) {
    if (prior.facilityId === facilityId) {
      for (const product of prior.products) productsById.set(product.id, product);
      for (const routing of prior.routings) routingByProduct.set(routing.product_id, routing);
    }
  }

  for (const product of delta.products) productsById.set(product.id, product);
  for (const routing of delta.routings) routingByProduct.set(routing.product_id, routing);

  for (const id of delta.deleted.product_ids) productsById.delete(id);
  for (const id of delta.deleted.routing_ids) {
    for (const [productId, routing] of routingByProduct) {
      if (routing.id === id) routingByProduct.delete(productId);
    }
  }

  // Prune routings whose product is no longer cached (evicted via tombstone).
  for (const [productId] of routingByProduct) {
    if (!productsById.has(productId)) routingByProduct.delete(productId);
  }

  return {
    facilityId,
    products: [...productsById.values()],
    routings: [...routingByProduct.values()],
    since: delta.next_since,
    syncedAt: now,
  };
}

/** The active routing for a product in a snapshot, if the facility has one. */
export function findRouting(
  snapshot: FloorSnapshot | undefined,
  productId: string,
): Routing | undefined {
  return snapshot?.routings.find(
    (routing) => routing.product_id === productId && routing.status === "active",
  );
}

/** The cached product card for an id, if present in the snapshot. */
export function findProduct(
  snapshot: FloorSnapshot | undefined,
  productId: string,
): Product | undefined {
  return snapshot?.products.find((product) => product.id === productId);
}

// ---- cache age --------------------------------------------------------------

export interface CacheAgeParts {
  key: string;
  n: number;
}

/**
 * Bucket a cache age (ms) into an i18n key + count. Pure so the age label is
 * unit-testable without a clock; the caller runs `t(key, { n })`.
 */
export function cacheAgeParts(ageMs: number): CacheAgeParts {
  const minutes = Math.floor(ageMs / 60_000);
  if (minutes < 1) return { key: "catalogue.floorLookup.ageJustNow", n: 0 };
  if (minutes < 60) return { key: "catalogue.floorLookup.ageMinutes", n: minutes };
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return { key: "catalogue.floorLookup.ageHours", n: hours };
  return { key: "catalogue.floorLookup.ageDays", n: Math.floor(hours / 24) };
}

// ---- persistence (guarded) --------------------------------------------------

function safeStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function isSnapshot(value: unknown): value is FloorSnapshot {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record["facilityId"] === "string" &&
    Array.isArray(record["products"]) &&
    Array.isArray(record["routings"]) &&
    typeof record["since"] === "string" &&
    typeof record["syncedAt"] === "number"
  );
}

/** Read the persisted snapshot for a facility, or undefined if none/corrupt. */
export function loadSnapshot(facilityId: string | undefined): FloorSnapshot | undefined {
  if (facilityId === undefined || facilityId.length === 0) return undefined;
  const storage = safeStorage();
  if (storage === undefined) return undefined;
  try {
    const raw = storage.getItem(snapshotKey(facilityId));
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return isSnapshot(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** Persist a merged snapshot (best-effort; storage failures are swallowed). */
export function saveSnapshot(snapshot: FloorSnapshot): void {
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    storage.setItem(snapshotKey(snapshot.facilityId), JSON.stringify(snapshot));
  } catch {
    // Quota / disabled storage: the in-memory query result still renders; the
    // next online sync retries persistence.
  }
}

/** The device's configured facility (provisioned once, remembered offline). */
export function loadFacilityId(): string | undefined {
  const storage = safeStorage();
  if (storage === undefined) return undefined;
  try {
    const value = storage.getItem(FACILITY_KEY);
    return value !== null && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

export function saveFacilityId(facilityId: string): void {
  const storage = safeStorage();
  if (storage === undefined) return;
  try {
    storage.setItem(FACILITY_KEY, facilityId);
  } catch {
    // Non-fatal — the facility stays in component state for this session.
  }
}
